import { parse as hpqParse } from "hpq";
import memoize from "memize";
import { attr, html, text, query, node, children, prop } from "./matchers";

/**
 * Creates a pipe function. Performs left-to-right function composition, where
 * each successive invocation is supplied the return value of the previous.
 *
 * @param {Function[]} functions Functions to pipe.
 */
function pipe(functions) {
  return (/** @type {unknown[]} */ ...args) => {
    return functions.reduce((prev, func) => [func(...prev)], args)[0];
  };
}

/**
 * Higher-order hpq matcher which enhances an attribute matcher to return true
 * or false depending on whether the original matcher returns undefined. This
 * is useful for boolean attributes (e.g. disabled) whose attribute values may
 * be technically falsey (empty string), though their mere presence should be
 * enough to infer as true.
 *
 * @param {Function} matcher Original hpq matcher.
 *
 * @return {Function} Enhanced hpq matcher.
 */
export const toBooleanAttributeMatcher = (matcher) =>
  pipe([
    matcher,
    // Expected values from `attr( 'disabled' )`:
    //
    // <input>
    // - Value:       `undefined`
    // - Transformed: `false`
    //
    // <input disabled>
    // - Value:       `''`
    // - Transformed: `true`
    //
    // <input disabled="disabled">
    // - Value:       `'disabled'`
    // - Transformed: `true`
    (value) => value !== undefined,
  ]);

/**
 * Parse a HTML string into DOM tree.
 *
 * @param {string|Node} innerHTML HTML string or already parsed DOM node.
 *
 * @return {Node} Parsed DOM node.
 */
function parseHtml(innerHTML) {
  return hpqParse(innerHTML, (h) => h);
}

/**
 * Returns an hpq matcher given a source object.
 *
 * @param {Object} sourceConfig Attribute Source object.
 *
 * @return {Function} A hpq Matcher.
 */
export const matcherFromSource = memoize((sourceConfig) => {
  switch (sourceConfig.source) {
    case "attribute":
      let matcher = attr(sourceConfig.selector, sourceConfig.attribute);
      if (sourceConfig.type === "boolean") {
        matcher = toBooleanAttributeMatcher(matcher);
      }

      return matcher;
    case "html":
      return html(sourceConfig.selector, sourceConfig.multiline);
    case "text":
      return text(sourceConfig.selector);
    case "children":
      return children(sourceConfig.selector);
    case "node":
      return node(sourceConfig.selector);
    case "query":
      const subMatchers = mapValues(sourceConfig.query, matcherFromSource);
      return query(sourceConfig.selector, subMatchers);
    case "tag":
      return pipe([
        prop(sourceConfig.selector, "nodeName"),
        (nodeName) => (nodeName ? nodeName.toLowerCase() : undefined),
      ]);
    default:
      // eslint-disable-next-line no-console
      console.error(`Unknown source type "${sourceConfig.source}"`);
  }
});

function parseWithAttributeSchema(innerHTML, attributeSchema) {
  return matcherFromSource(attributeSchema)(parseHtml(innerHTML));
}

/**
 * Given an attribute key, an attribute's schema, a block's raw content and the
 * commentAttributes returns the attribute value depending on its source
 * definition of the given attribute key.
 *
 * @param {string}      attributeKey      Attribute key.
 * @param {Object}      attributeSchema   Attribute's schema.
 * @param {string|Node} innerHTML         Block's raw content.
 * @param {Object}      commentAttributes Block's comment attributes.
 *
 * @return {*} Attribute value.
 */
export function getBlockProp(
  attributeKey,
  attributeSchema,
  innerHTML,
  commentAttributes,
) {
  let value;

  switch (attributeSchema.source) {
    // An undefined source means that it's an attribute serialized to the
    // block's "comment".
    case undefined:
      value = commentAttributes ? commentAttributes[attributeKey] : undefined;
      break;
    case "attribute":
    case "property":
    case "html":
    case "text":
    case "children":
    case "node":
    case "query":
    case "tag":
      value = parseWithAttributeSchema(innerHTML, attributeSchema);
      break;
  }

  if (value === undefined) {
    value = attributeSchema.default;
  }

  return value;
}

/**
 * Creates a new object with the same keys, but with `callback()` called as
 * a transformer function on each of the values.
 *
 * @param {Object}   obj      The object to transform.
 * @param {Function} callback The function to transform each object value.
 * @return {Array} Transformed object.
 */
const mapValues = (obj, callback) =>
  Object.entries(obj).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: callback(value),
    }),
    {},
  );

export function getBlockProps(blockTypeOrName, innerHTML, props = {}) {
  const doc = parseHtml(innerHTML);

  const blockProps = mapValues(props, (schema, key) =>
    getBlockProp(key, schema, doc, props),
  );

  return blockProps;
}
